<template>
  <f7-page name="save-to-buy-demand">
    <f7-navbar :title="$t('saveToBuy.requestProduct')" back-link />
    <AppLayout>
      <template #main>
        <f7-block inset strong class="stbd-intro">
          <div class="stbd-intro-title">
            {{ $t("saveToBuy.demandIntroTitle") }}
          </div>
          <div class="stbd-intro-sub">{{ $t("saveToBuy.demandIntroSub") }}</div>
        </f7-block>

        <f7-list inset strong media-list outline dividers form>
          <f7-list-input
            v-model:value="form.requestedName"
            :label="$t('saveToBuy.demandName')"
            :placeholder="$t('saveToBuy.demandNamePh')"
          />
          <f7-list-input
            v-model:value="form.description"
            type="textarea"
            resizable
            :label="$t('saveToBuy.demandDescription')"
            :placeholder="$t('saveToBuy.demandDescriptionPh')"
          />
          <f7-list-input
            v-model:value="form.referenceUrl"
            type="url"
            :label="$t('saveToBuy.referenceUrl')"
            :placeholder="'https://…'"
          />
          <f7-list-input
            v-model:value="form.requestedQuantity"
            type="number"
            :label="$t('saveToBuy.quantityNeeded')"
          />
          <f7-list-input
            v-model:value="form.targetUnitPrice"
            type="number"
            :label="$t('saveToBuy.targetUnitPrice')"
          ><template #media><span class="sel-listing-currency">{{form.currency}}</span></template></f7-list-input> 
          <f7-list-item :title="$t('saveToBuy.category')"   smart-select
        :smart-select-params="{
          openIn: isSmallScreen ? 'sheet' :'popup',
          searchbar: true,
          searchbarPlaceholder: $t('common.search'),
          closeOnSelect: true,
          swipeToClose:true,
          tabletFullScreen:false
        }">
              <select v-model="form.category" name="category">
                <option value="">{{ $t("saveToBuy.categoryNone") }}</option>
                <option v-for="c in categories" :key="c" :value="c">
                  {{ categoryLabel(c) }}

                </option>
              </select>
          </f7-list-item>
          <f7-list-input
            v-model:value="form.expiresAt"
            type="datetime-local"
            :label="$t('saveToBuy.ends')"
          />
        </f7-list>

        <f7-block>
          <f7-button fill large raised round :disabled="!canSubmit" @click="submit">
            {{ $t("saveToBuy.submitDemand") }}
          </f7-button>
        </f7-block>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { reactive, computed } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { OFFER_CATEGORIES, getCategoryLabel } from "../js/pinia-stores/marketplace.js";
import { useSaveToBuyStore } from "../js/pinia-stores/saveToBuy.js";
import { useSmallScreen } from '../js/composables/useMediaQuery.js'

import i18n from "../js/i18n.js";

const props = defineProps({
  groupId: { type: String, required: true },
});

const { t } = i18n.global;
const isSmallScreen = useSmallScreen().matches
const saveToBuyStore = useSaveToBuyStore();
const categories = Object.keys(OFFER_CATEGORIES);
const categoryLabel = (code) => getCategoryLabel(code);

const form = reactive({
  requestedName: "",
  description: "",
  referenceUrl: "",
  requestedQuantity: 1,
  targetUnitPrice: null,
  currency: "XOF",
  category: "",
  expiresAt: toLocalInput(Date.now() + 14 * 86400000),
});

const canSubmit = computed(
  () =>
    form.requestedName.trim().length > 0 &&
    Number(form.targetUnitPrice) > 0 &&
    Number(form.requestedQuantity) > 0 &&
    !!form.expiresAt,
);

function toLocalInput(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

async function submit() {
  if (!canSubmit.value) return;
  f7.preloader.show();
  try {
    await saveToBuyStore.createDemandRequest({
      requestedName: form.requestedName.trim(),
      description: form.description.trim(),
      referenceUrl: form.referenceUrl.trim() || null,
      category: form.category || null,
      targetUnitPrice: Number(form.targetUnitPrice),
      currency: form.currency,
      requestedQuantity: Number(form.requestedQuantity),
      sourceGroupId: props.groupId,
      expiresAt: new Date(form.expiresAt).toISOString(),
    });
    f7.preloader.hide();
    f7.toast.show({ text: t("saveToBuy.demandCreated"), closeTimeout: 2000 });
    f7.router.back();
  } catch (error) {
    f7.preloader.hide();
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  }
}
</script>

<style scoped>
.stbd-intro {
  background: color-mix(in srgb, var(--f7-theme-color) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--f7-theme-color) 16%, transparent);
  border-radius: 12px;
}
.stbd-intro-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
  line-height: 1.4;
}
.stbd-intro-sub {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 6px;
}
</style>
